import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

// Reads the NATIVE app version (CFBundleShortVersionString) once per app load and
// exposes a fail-safe version gate. This is the mechanism that lets web-side gates
// become version-aware: because the app remote-loads prod, a gate lifted in the web
// bundle would otherwise reach every installed device instantly, including old
// builds with no native plumbing.
//
// Predicate used at call sites:
//   canUseRestoredPath = !isNativePlatform() || atLeast('1.2.0')
// On web the first term grants access and atLeast is never consulted. On native the
// gate is kept until App.getInfo() resolves a version >= the threshold.
//
// FAIL-SAFE: atLeast() returns false on web, while the version is unresolved, when
// the plugin is missing (an old native binary with no @capacitor/app -> the bridge
// call rejects), and on any error. False means "keep the gate". Async, so native
// 1.2.0 devices see a brief flash-IN (gated then restored); flash-out never happens.
//
// SAB-488's geo flip needs this identical mechanism, so it reuses this hook.

// Resolved once, shared across every hook consumer.
//   undefined = not yet resolved, null = unavailable (web / old build / error)
let cachedVersion: string | null | undefined;
let inflight: Promise<string | null> | null = null;

function cmpSemver(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d;
  }
  return 0;
}

async function resolveNativeVersion(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const info = await App.getInfo();
    return info?.version ?? null;
  } catch {
    // Old native binary without @capacitor/app registered, or getInfo threw.
    return null;
  }
}

export interface NativeAppVersion {
  version: string | null;
  atLeast: (min: string) => boolean;
}

export function useNativeAppVersion(): NativeAppVersion {
  const [version, setVersion] = useState<string | null | undefined>(cachedVersion);

  useEffect(() => {
    if (cachedVersion !== undefined) {
      if (version !== cachedVersion) setVersion(cachedVersion);
      return;
    }
    if (!inflight) inflight = resolveNativeVersion().then((v) => { cachedVersion = v; return v; });
    let active = true;
    inflight.then((v) => { if (active) setVersion(v); });
    return () => { active = false; };
  }, [version]);

  const atLeast = (min: string): boolean =>
    Capacitor.isNativePlatform() && typeof version === 'string' && cmpSemver(version, min) >= 0;

  return { version: version ?? null, atLeast };
}
